/**
 * Drops API Client
 *
 * Handles all API calls related to public NFT drops (buyer-facing)
 */

import { apiClient, ApiResponse } from './client';
import type { XamanPayloadResponse } from '../types/signing';

// ==================== Types ====================

/** Phase 4 signing intent — carries client-signing txJson for joey-wc. */
export interface SigningIntentRef {
  intentId: string;
  provider: string;
  txJson?: Record<string, unknown>;
}

export interface UserAllowlistStatus {
  isOnAllowlist: boolean;
  isOnAllowlist2: boolean;
  mintLimit?: number;
  mintedCount?: number;
  mintLimit2?: number;
  mintedCount2?: number;
}

export interface Drop {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  thumbnail_url: string | null;
  banner_url: string | null;
  banner_position_y: number;
  creator_address: string;
  mint_phase: 'allowlist' | 'allowlist_2' | 'public' | null;
  status: 'ready' | 'active' | 'paused' | 'completed';
  total_supply: number | null;
  minted_count: number;
  available_count: number;
  mint_price_drops: number | null;
  allowlist_mint_price_drops: number | null;
  allowlist_2_mint_price_drops: number | null;
  allowlist_enabled: boolean;
  allowlist_2_enabled: boolean;
  allowlist_start_at: string | null;
  allowlist_2_start_at: string | null;
  public_start_at: string | null;
  end_at: string | null;
  // Extended fields for detail page
  transfer_fee?: number;
  website_url?: string | null;
  twitter_handle?: string | null;
  discord_url?: string | null;
  // User-specific data (null if not logged in)
  userAllowlistStatus?: UserAllowlistStatus | null;
  // Collection info (available after activation)
  collection_id?: string | null;
  collection_slug?: string | null;
  // Mint rewards flag
  has_mint_rewards?: boolean;
  // Launch type: 'standard' | 'composable' | 'edition'
  collection_type?: string;
}

/**
 * One artwork in an edition (print-style) drop.
 * Buyers pick an edition and mint a numbered copy of it.
 */
export interface DropEdition {
  id: string;
  edition_number: number;
  name: string;
  description: string | null;
  attributes: { trait_type: string; value: string | number }[] | null;
  image_url: string | null;
  thumbnail_url: string | null;
  animation_url: string | null;
  image_cid: string | null;
  /** Edition's own price; null = launch phase pricing applies */
  price_drops: number | null;
  effective_price_drops: number;
  effective_price_xrp: number;
  max_supply: number;
  minted_count: number;
  available_count: number;
  sold_out: boolean;
  sort_order: number | null;
}

export interface DropSampleItem {
  id: string;
  token_number: number;
  name: string | null;
  image_cid: string | null;
  status: string;
}

export interface DropProgress {
  total: number;
  minted: number;
  processing: number;
  available: number;
  failed: number;
}

export interface Eligibility {
  eligible: boolean;
  reason?: string;
  mintPhase: 'allowlist' | 'allowlist_2' | 'public' | null;
  priceDrops: number;
  maxAllowed: number;
  totalPriceDrops?: number;
  totalPriceXrp?: number;
  ambassadorMintAvailable?: boolean;
  ambassadorMintsClaimed?: number;
  ambassadorAllocationId?: string;
  /** Edition drops: remaining copies of the queried edition */
  editionAvailable?: number | null;
}

export interface MintRequest {
  mintIds: string[];
  batchId: string;
  quantity: number;
  totalPriceDrops: number;
  totalPriceXrp: number;
  reservationExpiresAt?: string;
  status?: string;
  message?: string;
  xaman?: XamanPayloadResponse;
  signingIntent?: SigningIntentRef;
}

export interface MintStatus {
  mint: {
    id: string;
    status: 'payment_pending' | 'payment_confirmed' | 'minting' | 'minted' | 'transferring' | 'completed' | 'failed' | 'expired';
    error_message: string | null;
    created_at: string;
  };
}

export interface UserMint {
  id: string;
  batch_id: string;
  status: string;
  payment_amount_drops: number;
  mint_price_drops: number;
  payment_tx_hash: string | null;
  nft_token_id: string | null;
  created_at: string;
  item: {
    token_number: number;
    name: string | null;
    image_cid: string | null;
  } | null;
}

// ==================== API Functions ====================

/**
 * List active drops
 */
export async function getDrops(params: {
  page?: number;
  limit?: number;
  status?: 'active' | 'upcoming' | 'completed';
  phase?: 'allowlist' | 'allowlist_2' | 'public';
  onMyAllowlist?: boolean;
} = {}): Promise<{
  drops: Drop[];
  total: number;
  page: number;
  limit: number;
}> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.status) searchParams.set('status', params.status);
  if (params.phase) searchParams.set('phase', params.phase);
  if (params.onMyAllowlist) searchParams.set('onMyAllowlist', 'true');

  const queryString = searchParams.toString();
  const endpoint = `/drops${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<ApiResponse<{
    drops: Drop[];
    total: number;
    page: number;
    limit: number;
  }>>(endpoint);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch drops');
  }

  return response.data;
}

/**
 * Get drop details by slug
 */
export async function getDrop(slug: string): Promise<{
  drop: Drop;
  progress: DropProgress;
  sampleItems: DropSampleItem[];
  eligibility: Eligibility | null;
}> {
  const response = await apiClient.get<ApiResponse<{
    drop: Drop;
    progress: DropProgress;
    sampleItems: DropSampleItem[];
    eligibility: Eligibility | null;
  }>>(`/drops/${encodeURIComponent(slug)}`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch drop');
  }

  return response.data;
}

/**
 * Get editions gallery for an edition (print-style) drop
 */
export async function getDropEditions(slug: string): Promise<DropEdition[]> {
  const response = await apiClient.get<ApiResponse<{ editions: DropEdition[] }>>(
    `/drops/${encodeURIComponent(slug)}/editions`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch editions');
  }

  return response.data.editions;
}

/**
 * Check mint eligibility
 */
export async function checkEligibility(
  slug: string,
  quantity: number = 1,
  editionId?: string | null
): Promise<Eligibility> {
  const editionParam = editionId ? `&editionId=${encodeURIComponent(editionId)}` : '';
  const response = await apiClient.get<ApiResponse<Eligibility>>(
    `/drops/${encodeURIComponent(slug)}/eligibility?quantity=${quantity}${editionParam}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to check eligibility');
  }

  return response.data;
}

/**
 * Initiate a mint request
 */
export async function requestMint(
  slug: string,
  quantity: number = 1,
  referralCode?: string | null,
  ambassadorAllocationId?: string | null,
  editionId?: string | null
): Promise<MintRequest> {
  const body: Record<string, any> = { quantity };
  if (referralCode) {
    body.referral_code = referralCode;
  }
  if (ambassadorAllocationId) {
    body.ambassadorAllocationId = ambassadorAllocationId;
  }
  if (editionId) {
    body.editionId = editionId;
  }
  const response = await apiClient.post<ApiResponse<MintRequest>>(
    `/drops/${encodeURIComponent(slug)}/mint`,
    body
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to initiate mint');
  }

  return response.data;
}

/**
 * Check mint status
 */
export async function getMintStatus(
  slug: string,
  mintId: string
): Promise<MintStatus> {
  const response = await apiClient.get<ApiResponse<MintStatus>>(
    `/drops/${encodeURIComponent(slug)}/mint/${mintId}/status`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get mint status');
  }

  return response.data;
}

export interface ResumeResponse {
  batchId: string;
  quantity: number;
  totalPriceDrops: number;
  totalPriceXrp: number;
  reservationExpiresAt: string;
  remainingSeconds: number;
  xaman?: XamanPayloadResponse;
  signingIntent?: SigningIntentRef;
}

export interface CancelResponse {
  message: string;
  batchId: string;
  itemsReleased: number;
}

/**
 * Resume a pending mint by generating a new Xaman payment payload
 */
export async function resumeMint(
  slug: string,
  batchId: string
): Promise<ResumeResponse> {
  const response = await apiClient.post<ApiResponse<ResumeResponse>>(
    `/drops/${encodeURIComponent(slug)}/mint/${batchId}/resume`,
    {}
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to resume mint');
  }

  return response.data;
}

/**
 * Cancel a pending mint after safety check
 */
export async function cancelMint(
  slug: string,
  batchId: string
): Promise<CancelResponse> {
  const response = await apiClient.post<ApiResponse<CancelResponse & { paymentTxHash?: string }>>(
    `/drops/${encodeURIComponent(slug)}/mint/${batchId}/cancel`,
    {}
  );

  if (!response.success) {
    // Check for payment detected response
    if (response.error === 'Payment detected') {
      throw new Error('PAYMENT_DETECTED');
    }
    throw new Error(response.error || 'Failed to cancel mint');
  }

  if (!response.data) {
    throw new Error('Failed to cancel mint');
  }

  return response.data;
}

/**
 * Get user's mints for a drop
 */
export async function getMyMints(
  slug: string,
  params: { page?: number; limit?: number } = {}
): Promise<{
  mints: UserMint[];
  total: number;
  page: number;
  limit: number;
}> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const endpoint = `/drops/${encodeURIComponent(slug)}/my-mints${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<ApiResponse<{
    mints: UserMint[];
    total: number;
    page: number;
    limit: number;
  }>>(endpoint);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch mints');
  }

  return response.data;
}

// ==================== Allowlist Registration ====================

export interface RequirementCheckResult {
  type: string;
  passed: boolean | null;
  label: string;
  description?: string;
  actionUrl?: string;
  needsInput?: boolean;
  inputField?: {
    key: string;
    label: string;
    placeholder: string;
  };
}

export interface PaidRegistrationPayment {
  paymentId: string;
  quantity: number;
  pricePerSlotDrops: number;
  totalBaseDrops: number;
  platformFeeDrops: number;
  paymentAmountDrops: number;
  paymentAmountXrp: number;
  expiresAt: string;
  xaman: XamanPayloadResponse;
}

export interface PaidRegistrationStatus {
  paymentId: string;
  status: 'pending' | 'confirmed' | 'expired' | 'failed';
  quantity: number;
  paymentAmountDrops: number;
  confirmedAt?: string;
  errorMessage?: string;
}

export interface RegistrationPhaseInfo {
  phase: 'allowlist' | 'allowlist_2';
  phaseName: string;
  isOpen: boolean;
  closedReason: string | null;
  registrationCount: number;
  registrationLimit: number | null;
  spotsRemaining: number | null;
  defaultMints: number;
  deadline: string | null;
  userStatus: 'not_logged_in' | 'not_registered' | 'registered';
  userMintLimit?: number;
  requirements?: RequirementCheckResult[];
  allRequirementsMet?: boolean;
  paidRegistration?: {
    priceDrops: number;
    maxQuantity: number | null;
    priceXrp: number;
    serviceFeePercent: number;
  };
  pendingPayment?: PaidRegistrationStatus;
}

export interface RegistrationInfo {
  drop: {
    name: string;
    slug: string;
    description: string | null;
    thumbnail_url: string | null;
    banner_url: string | null;
    banner_position_y: number;
    status: string;
    total_supply: number | null;
    minted_count: number;
    mint_price_drops: number | null;
    allowlist_mint_price_drops: number | null;
    website_url: string | null;
    twitter_handle: string | null;
    discord_url: string | null;
    allowlist_start_at: string | null;
    allowlist_2_start_at: string | null;
    public_start_at: string | null;
  };
  phases: RegistrationPhaseInfo[];
}

export interface RegisterResult {
  alreadyRegistered: boolean;
  phase: 'allowlist' | 'allowlist_2';
}

/**
 * Get registration info for a drop
 */
export async function getRegistrationInfo(slug: string): Promise<RegistrationInfo> {
  const response = await apiClient.get<ApiResponse<RegistrationInfo>>(
    `/drops/${encodeURIComponent(slug)}/registration`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch registration info');
  }

  return response.data;
}

/**
 * Register for a drop's allowlist
 */
export async function registerForAllowlist(
  slug: string,
  phase: 'allowlist' | 'allowlist_2',
  twitterUsername?: string
): Promise<RegisterResult> {
  const response = await apiClient.post<ApiResponse<RegisterResult>>(
    `/drops/${encodeURIComponent(slug)}/register`,
    { phase, ...(twitterUsername ? { twitter_username: twitterUsername } : {}) }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to register');
  }

  return response.data;
}

/**
 * Initiate a paid registration payment
 */
export async function initiateRegistrationPayment(
  slug: string,
  phase: 'allowlist' | 'allowlist_2',
  quantity: number,
  twitterUsername?: string,
): Promise<PaidRegistrationPayment> {
  const body: Record<string, any> = { phase, quantity };
  if (twitterUsername) body.twitter_username = twitterUsername;
  const response = await apiClient.post<ApiResponse<PaidRegistrationPayment>>(
    `/drops/${encodeURIComponent(slug)}/register-payment`,
    body
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to initiate registration payment');
  }

  return response.data;
}

/**
 * Get registration payment status
 */
export async function getRegistrationPaymentStatus(
  slug: string,
  paymentId: string,
): Promise<PaidRegistrationStatus> {
  const response = await apiClient.get<ApiResponse<PaidRegistrationStatus>>(
    `/drops/${encodeURIComponent(slug)}/register-payment/${paymentId}/status`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get registration payment status');
  }

  return response.data;
}

// ==================== Utility Functions ====================

/**
 * Get IPFS gateway URL for an image CID
 */
export function getIpfsImageUrl(cid: string | null): string | null {
  if (!cid) return null;
  // Use our self-hosted IPFS gateway
  return `https://oversolve-ipfs.fly.dev/ipfs/${cid}`;
}

/**
 * Format drops to XRP display
 */
export function formatDropsToXrp(drops: number | null): string {
  if (drops === null || drops === undefined) return 'Not set';
  if (drops === 0) return 'Free';
  const xrp = drops / 1_000_000;
  return `${xrp.toLocaleString()} XRP`;
}

/**
 * Get progress percentage
 */
export function getMintProgress(minted: number, total: number | null): number {
  if (!total || total === 0) return 0;
  return Math.round((minted / total) * 100);
}

/**
 * Format mint phase display
 */
export function formatMintPhase(phase: 'allowlist' | 'allowlist_2' | 'public' | null): string {
  switch (phase) {
    case 'allowlist':
      return 'Allowlist Phase 1';
    case 'allowlist_2':
      return 'Allowlist Phase 2';
    case 'public':
      return 'Public';
    default:
      return 'Not started';
  }
}

/**
 * Get time until start
 */
export function getTimeUntilStart(
  allowlistStart: string | null,
  publicStart: string | null,
  allowlistEnabled: boolean,
  allowlist2Start?: string | null,
  allowlist2Enabled?: boolean
): { started: boolean; startsAt: Date | null; label: string } {
  const now = new Date();

  // Check allowlist phase 1 start first if enabled
  if (allowlistEnabled && allowlistStart) {
    const alStart = new Date(allowlistStart);
    if (now < alStart) {
      return { started: false, startsAt: alStart, label: allowlist2Enabled ? 'Phase 1 starts in' : 'Allowlist starts in' };
    }
  }

  // Check allowlist phase 2 start if enabled
  if (allowlist2Enabled && allowlist2Start) {
    const al2Start = new Date(allowlist2Start);
    if (now < al2Start) {
      return { started: false, startsAt: al2Start, label: 'Phase 2 starts in' };
    }
  }

  // Check public start
  if (publicStart) {
    const pubStart = new Date(publicStart);
    if (now < pubStart) {
      return { started: false, startsAt: pubStart, label: 'Public mint starts in' };
    }
  }

  return { started: true, startsAt: null, label: 'Started' };
}

/**
 * Get mint status display
 */
export function getMintStatusDisplay(status: string, isFree = false): {
  label: string;
  color: 'gray' | 'yellow' | 'blue' | 'green' | 'red';
} {
  switch (status) {
    case 'payment_pending':
      return { label: isFree ? 'Confirming Mint' : 'Awaiting Payment', color: 'yellow' };
    case 'payment_confirmed':
      return { label: isFree ? 'Mint Confirmed' : 'Payment Confirmed', color: 'blue' };
    case 'minting':
      return { label: 'Minting...', color: 'blue' };
    case 'minted':
      return { label: 'Minted', color: 'blue' };
    case 'transferring':
      return { label: 'Transferring', color: 'blue' };
    case 'completed':
      return { label: 'Complete', color: 'green' };
    case 'failed':
      return { label: 'Failed', color: 'red' };
    case 'expired':
      return { label: 'Expired', color: 'gray' };
    default:
      return { label: status, color: 'gray' };
  }
}
